import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemLink from "./list-item-link";
import RenderOnAuthenticated from "./Permissions/render-on-authenticated";
import * as PropTypes from "prop-types";
import React, {FunctionComponent, Suspense, useReducer} from "react";
import {
    Box,
    CircularProgress,
    Collapse,
    Link,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    SxProps,
    Toolbar
} from "@mui/material";
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import RouteService from "../services/route-service";
import RenderOnRole from "./Permissions/render-on-role";
import {RenderOnFeatureFlag} from "./Common/render-on-feature-flag";
import {AnimalRecordStatusValues} from "../api/generated/herd-animal";
import {PANON_ADMIN, REGISTRY_WARDEN} from "./Permissions/Roles";
import {HerdQuickList} from "./Navigation/herd-quick-list";
import {AdminPanelSettings, Home, Key, ManageAccounts, Settings} from "@mui/icons-material";
import HelpIcon from '@mui/icons-material/Help';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AuthService from "../services/auth-service";
import {BreedersIcon, DocumentsIcon, HerdIcon, RegistryIcon} from "../icons";
import {useRegistriesOfMember} from "./Registry/hooks/use-registries";
import {LoadingSuspense} from "./Common/loading-suspense";

interface MenuProperties {
    onClick?: () => void
    sx: SxProps
}

const LoadingFallback = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress/>
    </Box>
);

const useToggle = (initialValue: boolean) => useReducer(x => !x, initialValue);

const RegistryLinks = ({onClick}: { onClick: () => void }) => {
    const {data: registries = []} = useRegistriesOfMember();

    return (
        <>
            {registries.map(registry => (
                <ListItemLink
                    key={registry.id}
                    onClickHandler={onClick}
                    to={RouteService.expand(RouteService.REGISTRY_ANIMAL_LIST, {registryId: registry.id})}
                    primary={registry.name}/>
            ))}
        </>
    );
};

const NavigationMenu: FunctionComponent<MenuProperties> = ({onClick = (() => undefined), sx}) => {

    const [openAdminArea, toggleOpenAdminArea] = useToggle(false);
    const [openSettings, toggleOpenSettings] = useToggle(false);

    return <Box sx={sx}>
        <Toolbar/>
        <Divider/>
        <List component="nav" sx={{'& .MuiListItemIcon-root': {minWidth:'32px'}}}>
            <ListItemLink onClickHandler={onClick}
                          to={RouteService.HOME}
                          icon={<Home/>}
                          primary={AuthService.isLoggedIn() ? 'Dashboard' : 'Login'}/>
            <RenderOnAuthenticated>
                <ListItemLink onClickHandler={onClick}
                              to={RouteService.expand(RouteService.HERD_GROUP, {groupId: "all", recordStatus: AnimalRecordStatusValues.HERD})}
                              icon={<HerdIcon/>}
                              primary="Meine Herde"/>
                <List disablePadding sx={{ pl: 5 }}>
                    <ListItemLink
                        onClickHandler={onClick}
                        to={RouteService.HERD_PREGNANCIES_LIST}
                        primary="Trächtigkeiten"/>
                    <ListItemLink
                        onClickHandler={onClick}
                        to={RouteService.expand(RouteService.HERD_GROUP, {groupId: "all", recordStatus: AnimalRecordStatusValues.INACTIVE})}
                        primary="Inaktive Tiere"/>
                    <ListItemLink
                        onClickHandler={onClick}
                        to={RouteService.ANIMAL_OWNERSHIP_TRANSFERS}
                        primary="Transfers"/>
                </List>

                <ListItemLink onClickHandler={onClick}
                              to={RouteService.REGISTRY_LIST}
                              icon={<RegistryIcon/>}
                              primary="Register"/>
                <List disablePadding sx={{ pl: 5 }}>
                    <ListItemLink onClickHandler={onClick} to={RouteService.REGISTRY_MY_REGISTRATIONS_LIST} primary="Meine Anmeldungen"/>
                    <LoadingSuspense fallback={<></>} error={<></>}>
                        <RegistryLinks onClick={onClick} />
                    </LoadingSuspense>
                    <RenderOnRole roles={[REGISTRY_WARDEN, PANON_ADMIN]}>
                        <ListItemLink onClickHandler={onClick} to={RouteService.REGISTRY_SEARCH} primary="Registeradministration"/>
                        <ListItemLink onClickHandler={onClick} to={RouteService.REGISTRY_ANIMAL_REVIEW_LIST} primary="Registeranmeldungen"/>
                    </RenderOnRole>
                </List>

                <RenderOnRole roles={[PANON_ADMIN]}>
                    <ListItemLink to={RouteService.DOCUMENTS_LIST} icon={<DocumentsIcon/>} primary="Meine Dokumente"/>
                </RenderOnRole>

                <ListItemLink onClickHandler={onClick}
                              to={RouteService.BREEDERS_LIST}
                              icon={<BreedersIcon/>}
                              primary="Züchter"/>

                <RenderOnFeatureFlag  featureFlag={"ADMIN_AREA"}>
                    <RenderOnRole roles={[PANON_ADMIN]}>
                        <ListItemButton onClick={toggleOpenAdminArea}>
                            <ListItemIcon><AdminPanelSettings/></ListItemIcon>
                            <ListItemText primary="Admin Area" />
                            {openAdminArea ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={openAdminArea} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding sx={{ pl: 5 }}>
                                <ListItemLink onClickHandler={onClick} to={RouteService.ADMIN_LINK_ACCOUNT_WITH_BREEDER} primary="Accounts Verlinken"/>
                                <ListItemLink onClickHandler={onClick} to={RouteService.ADMIN_BREEDER_LIST} primary="Züchterliste"/>
                            </List>
                        </Collapse>
                    </RenderOnRole>
                </RenderOnFeatureFlag>

                <ListItemButton onClick={toggleOpenSettings}>
                    <ListItemIcon><Settings/></ListItemIcon>
                    <ListItemText primary="Einstellungen" />
                    {openSettings ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openSettings} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: 5 }}>
                        <ListItemLink onClickHandler={onClick} to={RouteService.BREEDER_BASE_DATA} primary="Züchterdaten" icon={<ManageAccounts fontSize="small"/>}/>
                        <ListItemButton onClick={onClick}>
                            <ListItemIcon><Key fontSize="small"/></ListItemIcon>
                            <ListItemText>
                                <Link href={AuthService.getAccountUrl()} underline="none" color="inherit">Profil</Link>
                            </ListItemText>
                        </ListItemButton>
                        <RenderOnRole roles={[PANON_ADMIN]}>
                            <ListItemLink onClickHandler={onClick} to={RouteService.BREEDER_SUBSCRIPTION_OVERVIEW} primary="Abo-Einstellungen" icon={<CreditCardIcon fontSize="small"/>}/>
                        </RenderOnRole>
                        </List>
                </Collapse>
            </RenderOnAuthenticated>
            <ListItemLink onClickHandler={onClick} primary="Hilfe" to={RouteService.HELP} icon={<HelpIcon/>} />
        </List>
        <RenderOnAuthenticated>
            <Divider />
            <Suspense fallback={<LoadingFallback />}>
                <HerdQuickList/>
            </Suspense>
        </RenderOnAuthenticated>
    </Box>;
}

NavigationMenu.propTypes = {
    onClick: PropTypes.func.isRequired
};

export default NavigationMenu;
